import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import EmptyState, { CardGridSkeleton } from "@/components/base/EmptyState";
import { usePageTitle, useSimulatedLoading } from "@/hooks/usePageMeta";
import {
  allGenres,
  allYears,
  defaultFilters,
  filterTitles,
  searchTitles,
  titles,
  type TitleFilters,
} from "@/utils/catalog";

const hotKeywords = ["深海", "悬疑", "科幻", "治愈", "程砚舟", "港剧"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const navigate = useNavigate();
  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<TitleFilters>(defaultFilters);
  const [kind, setKind] = useState<"all" | "movie" | "tv">("all");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageTitle(
    keyword ? `"${keyword}" 的搜索结果 - CC视频` : "搜索影视作品 - CC视频",
    `在CC视频搜索影视作品，按影片类型与上映年份筛选，快速找到你想看的电影或电视剧。`,
  );

  const matched = useMemo(() => {
    const base = keyword.trim() ? searchTitles(keyword) : titles;
    const byKind = kind === "all" ? base : base.filter((t) => t.kind === kind);
    return filterTitles(byKind, { year: filters.year, genre: filters.genre, sort: "popular" });
  }, [keyword, filters.year, filters.genre, kind]);

  const loading = useSimulatedLoading([keyword, kind, filters.year, filters.genre], 340);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const activeFilterCount =
    (filters.year !== "全部" ? 1 : 0) + (filters.genre !== "全部" ? 1 : 0) + (kind !== "all" ? 1 : 0);

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
            <span className="text-accent-400">搜索</span>
          </nav>
          <h1 className="font-heading text-xl md:text-3xl font-black text-background-50 tracking-tight">
            {keyword ? (
              <>
                搜索「<span className="text-accent-400">{keyword}</span>」
              </>
            ) : (
              "搜索影视作品"
            )}
          </h1>
          <p className="mt-2 text-sm text-background-400">
            支持片名、导演、演员与类型的模糊匹配
          </p>

          <form onSubmit={submit} className="mt-5 flex gap-2 max-w-2xl">
            <div className="flex-1 flex items-center h-12 rounded-md bg-background-50/10 border border-background-50/25 px-3 focus-within:border-accent-400 transition-colors">
              <i className="ri-search-line text-background-300 text-base w-4 h-4 flex items-center justify-center" />
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，例如：深海蓝线"
                className="flex-1 min-w-0 bg-transparent px-2 text-sm text-background-50 outline-none placeholder:text-background-300/80"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-background-400">热门搜索</span>
            {hotKeywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setInput(k);
                  setParams({ q: k });
                }}
                className="px-2.5 h-7 rounded-full border border-background-50/25 text-[11px] text-background-200 hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 py-6 md:py-8">
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden mb-6">
          <div className="flex items-start gap-3 px-3 md:px-5 py-3 border-b border-background-200">
            <span className="w-10 shrink-0 text-xs text-foreground-500 pt-1.5">类型</span>
            <div className="flex-1 flex flex-wrap gap-1.5">
              {[
                { value: "all", label: "全部" },
                { value: "movie", label: "电影" },
                { value: "tv", label: "电视剧" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setKind(opt.value as "all" | "movie" | "tv")}
                  className={`h-7 px-2.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    kind === opt.value
                      ? "bg-accent-500 text-primary-950 font-medium"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3 px-3 md:px-5 py-3 border-b border-background-200">
            <span className="w-10 shrink-0 text-xs text-foreground-500 pt-1.5">题材</span>
            <div className="flex-1 flex flex-wrap gap-1.5">
              {allGenres.slice(0, 14).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setFilters((p) => ({ ...p, genre: g }))}
                  className={`h-7 px-2.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    filters.genre === g
                      ? "bg-accent-500 text-primary-950 font-medium"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3 px-3 md:px-5 py-3">
            <span className="w-10 shrink-0 text-xs text-foreground-500 pt-1.5">年份</span>
            <div className="flex-1 flex flex-wrap gap-1.5">
              {allYears.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setFilters((p) => ({ ...p, year: y }))}
                  className={`h-7 px-2.5 rounded-md text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    filters.year === y
                      ? "bg-accent-500 text-primary-950 font-medium"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="text-sm text-foreground-600">
            共找到{" "}
            <span className="font-label font-semibold text-foreground-950">{matched.length}</span>{" "}
            部{keyword ? `与「${keyword}」相关的作品` : "作品"}
          </p>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setFilters(defaultFilters);
                setKind("all");
              }}
              className="h-8 px-3 rounded-md text-xs text-foreground-600 border border-background-300 hover:text-foreground-950 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line mr-1" />
              重置筛选
            </button>
          )}
        </div>

        {loading ? (
          <CardGridSkeleton count={10} />
        ) : matched.length === 0 ? (
          <EmptyState
            icon="ri-file-search-line"
            title="没有找到匹配的作品"
            description={
              keyword
                ? `没有与「${keyword}」相关的影视内容，换个关键词试试，或者直接浏览分类列表。`
                : "当前筛选条件下没有作品，试试重置筛选条件。"
            }
          >
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setFilters(defaultFilters);
                  setKind("all");
                }}
                className="h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                重置筛选
              </button>
              <Link
                to="/"
                className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </EmptyState>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
            {matched.map((item, i) => (
              <div key={item.id} className="cc-fade-up">
                <FilmCard item={item} index={i} />
              </div>
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}